import { useState } from 'react';

interface SecurityCardProps {
  icon: string;
  title: string;
  description: string;
  detail: string;
  delay?: number;
}

const SecurityCard = ({ icon, title, description, detail, delay = 0 }: SecurityCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className="group relative overflow-hidden rounded-3xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
      style={{ animationDelay: `${delay}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Glow Effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 via-cyan-500 to-emerald-500 rounded-3xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
      
      {/* Card Background */}
      <div className="relative bg-gradient-to-br from-gray-50 dark:from-zinc-800/80 to-gray-100 dark:to-zinc-900/80 backdrop-blur-xl rounded-3xl border border-gray-200 dark:border-zinc-700/50 group-hover:border-emerald-500/30 transition-colors duration-500 h-full">
        {/* Top Accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-cyan-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity rounded-t-3xl"></div>
        
        {/* Content */}
        <div className="p-7">
          {/* Icon */}
          <div className="relative w-16 h-16 mb-5">
            {/* Animated Ring */}
            <div className="absolute inset-0 border-2 border-emerald-500/30 rounded-2xl animate-[spin_8s_linear_infinite] group-hover:border-emerald-500/60 transition-colors"></div>
            <div className="absolute inset-1 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20 group-hover:shadow-emerald-500/40 transition-shadow">
              <span className="text-2xl">{icon}</span>
            </div>
          </div>
          
          <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
            {title}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            {description}
          </p>
          
          {/* Expandable Detail */}
          <div className={`overflow-hidden transition-all duration-300 ${isHovered ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="pt-4 border-t border-gray-200 dark:border-zinc-700">
              <p className="text-sm text-gray-500 dark:text-gray-500 font-mono bg-gray-100 dark:bg-zinc-800 rounded-lg p-3">
                {detail}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function SecurityFeature() {
  const securityFeatures = [
    {
      icon: '💾',
      title: '로컬 저장',
      description: "AI의 '기억'과 대화 기록이 당신의 컴퓨터에만 저장됩니다.",
      detail: '~/.openclaw/workspace/MEMORY.md',
    },
    {
      icon: '🔍',
      title: '투명한 구조',
      description: '저장된 파일을 직접 열어보고 수정할 수 있습니다.',
      detail: '모든 데이터가 일반 텍스트 파일로 저장',
    },
    {
      icon: '🗑️',
      title: '완전한 삭제',
      description: '폴더를 삭제하면 모든 흔적이 즉시 사라집니다.',
      detail: 'rm -rf ~/.openclaw/ — 끝!',
    },
    {
      icon: '🔐',
      title: '안전한 저장',
      description: '민감한 API 키와 토큰은 OS 보안 저장소에 암호화됩니다.',
      detail: 'macOS Keychain / Windows Credential Manager',
    },
  ];

  return (
    <section id="security" className="relative py-24 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-pure-white dark:from-space-black via-emerald-50/30 dark:via-emerald-950/10 to-pure-white dark:to-space-black"></div>
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]">
          <div className="w-full h-full" style={{
            backgroundImage: `linear-gradient(rgba(16, 185, 129, 1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(16, 185, 129, 1) 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
      </div>
      
      {/* Floating Accents */}
      <div className="absolute top-20 left-10 w-40 h-40 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-full blur-[100px] animate-pulse-soft"></div>
      <div className="absolute bottom-20 right-10 w-32 h-32 bg-cyan-500/10 dark:bg-cyan-500/15 rounded-full blur-[80px] animate-pulse-soft" style={{ animationDelay: '2s' }}></div>
      
      {/* Shield Icon Animation */}
      <div className="absolute top-32 right-[15%] opacity-5 dark:opacity-10 animate-float hidden lg:block">
        <svg className="w-24 h-24 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      </div>
      
      <div className="relative z-10 section-container">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 dark:from-emerald-500/20 dark:to-cyan-500/20 backdrop-blur-sm px-5 py-2.5 rounded-full text-sm font-medium mb-6 border border-emerald-200 dark:border-emerald-500/20">
            <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span className="text-emerald-700 dark:text-emerald-400">프라이버시 & 보안</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-emerald-600 via-cyan-600 to-emerald-600 dark:from-emerald-400 dark:via-cyan-400 dark:to-emerald-400 bg-clip-text text-transparent">
              당신의 데이터, 당신의 통제
            </span>
          </h2>
          
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
            moldClaw는 로컬에서 실행됩니다.<br className="hidden sm:block" />
            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">대화 기록과 AI의 "기억"은 당신의 컴퓨터에만 저장됩니다.</span>
          </p>
        </div>
        
        {/* Security Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
          {securityFeatures.map((feature, index) => (
            <SecurityCard
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              detail={feature.detail}
              delay={index * 100}
            />
          ))}
        </div>
        
        {/* Trust Comparison */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-wrap gap-4 justify-center items-center text-sm">
            <div className="flex items-center gap-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-4 py-2 rounded-full">
              <span className="text-emerald-500">✓</span>
              <span>서버에 대화 저장 안 함</span>
            </div>
            <div className="flex items-center gap-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-4 py-2 rounded-full">
              <span className="text-emerald-500">✓</span>
              <span>오픈소스로 검증 가능</span>
            </div>
            <div className="flex items-center gap-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-4 py-2 rounded-full">
              <span className="text-emerald-500">✓</span>
              <span>언제든 완전 삭제</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
